import React from 'react';
import { RecipeTriggerType } from '../../types/recipe';

export type RecipeExecutionGuideStatus =
  | 'checking'
  | 'queued'
  | 'running'
  | 'finished'
  | 'webhook_ready'
  | 'scheduled'
  | 'needs_user'
  | 'failed';

type RecipeExecutionGuideProps = {
  status: RecipeExecutionGuideStatus;
  triggerType: RecipeTriggerType | null;
  nextRunAt?: string | null;
  errorMessage?: string | null;
  minimized: boolean;
  onMinimize: () => void;
  onClose: () => void;
  onRestore: () => void;
  onAskChef: () => void;
  onViewLiveOutput: () => void;
  onKeepEditing: () => void;
};

type GuideView = {
  eyebrow: string;
  title: string;
  message?: string;
  step: number;
  steps: string[];
  complete?: boolean;
};

const guideView = (
  status: RecipeExecutionGuideStatus,
  nextRunAt?: string | null,
  errorMessage?: string | null
): GuideView => {
  switch (status) {
    case 'queued':
      return {
        eyebrow: 'Waiting for a chef',
        title: 'Your recipe is queued',
        message: 'Kitchen has accepted the recipe and will begin as soon as a chef is available.',
        step: 2,
        steps: ['Identify trigger', 'Queue recipe', 'Run recipe', 'Review result'],
      };
    case 'running':
      return {
        eyebrow: 'Chef at work',
        title: 'Your recipe is running',
        step: 3,
        steps: ['Identify trigger', 'Queue recipe', 'Run recipe', 'Review result'],
      };
    case 'finished':
      return {
        eyebrow: 'All done',
        title: 'Recipe completed',
        message: 'The result and full execution history are ready to review.',
        step: 4,
        steps: ['Identify trigger', 'Queue recipe', 'Run recipe', 'Review result'],
        complete: true,
      };
    case 'webhook_ready':
      return {
        eyebrow: 'Webhook trigger found',
        title: 'Waiting for a webhook',
        message: 'Setup is complete. The recipe will run when a webhook request arrives.',
        step: 2,
        steps: ['Identify trigger', 'Connect webhook'],
        complete: true,
      };
    case 'scheduled': {
      const formatted = nextRunAt
        ? new Date(nextRunAt).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
        : null;
      return {
        eyebrow: 'Scheduled trigger found',
        title: formatted ? `Next run: ${formatted}` : 'Your recipe is scheduled',
        message: 'Setup is complete. Kitchen will start the recipe automatically.',
        step: 2,
        steps: ['Identify trigger', 'Schedule recipe'],
        complete: true,
      };
    }
    case 'needs_user':
      return {
        eyebrow: 'Chef needs your help',
        title: 'One question before scheduling',
        message: 'Open Chef to answer the scheduling question. Kitchen will continue after you reply.',
        step: 1,
        steps: ['Answer question', 'Schedule recipe'],
      };
    case 'failed':
      return {
        eyebrow: 'Could not start',
        title: 'Kitchen needs another try',
        message: errorMessage || 'Kitchen could not prepare this recipe. Nothing was started.',
        step: 1,
        steps: ['Start recipe'],
      };
    case 'checking':
    default:
      return {
        eyebrow: 'Understanding recipe',
        title: 'Checking your recipe…',
        message: 'Kitchen is identifying how this recipe should be triggered.',
        step: 1,
        steps: ['Identify trigger'],
      };
  }
};

const Spinner = () => (
  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const StepSpinner = () => (
  <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const RecipeExecutionGuide: React.FC<RecipeExecutionGuideProps> = ({
  status,
  triggerType,
  nextRunAt,
  errorMessage,
  minimized,
  onMinimize,
  onClose,
  onRestore,
  onAskChef,
  onViewLiveOutput,
  onKeepEditing,
}) => {
  const view = guideView(status, nextRunAt, errorMessage);
  const canMinimize = ['checking', 'queued', 'running'].includes(status);
  const liveOutputReady = triggerType === 'manual' && ['queued', 'running', 'finished'].includes(status);
  const actionIntro = status === 'running'
    ? {
        title: 'While your recipe cooks',
        message: 'You can do any of the following without interrupting this run.',
      }
    : status === 'finished'
      ? {
          title: 'Your recipe is ready',
          message: 'Review the result, ask a question, or continue editing.',
        }
      : status === 'scheduled'
        ? {
            title: 'Your schedule is ready',
            message: 'Review the schedule, ask a question, or continue editing.',
          }
        : status === 'webhook_ready'
          ? {
              title: 'Your webhook is ready',
              message: 'The recipe will start after the webhook request arrives.',
            }
          : status === 'needs_user'
            ? {
                title: 'Chef is waiting for your answer',
                message: 'Answer Chef to finish scheduling this recipe.',
              }
            : status === 'failed'
              ? {
                  title: 'The recipe did not start',
                  message: 'Ask Chef for help or continue editing before trying again.',
                }
              : status === 'queued'
                ? {
                    title: 'You can keep working',
                    message: 'These actions won’t interrupt your queued recipe.',
                  }
                : {
                    title: 'You can keep working',
                    message: 'These actions won’t interrupt Kitchen while it checks your recipe.',
                  };
  const dismissForAction = canMinimize ? onMinimize : onClose;

  if (minimized) {
    return (
      <button
        type="button"
        onClick={onRestore}
        className="fixed bottom-4 right-4 z-[9999] flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs font-semibold text-gray-800 shadow-xl transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label="Restore recipe progress"
      >
        <span className={`h-2 w-2 rounded-full ${view.complete ? 'bg-emerald-500' : status === 'failed' ? 'bg-red-500' : 'bg-indigo-500'}`} />
        <span>{view.title.replace('…', '')}</span>
        <svg className="h-3.5 w-3.5 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="m6 12 4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-labelledby="recipe-execution-guide-title">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="p-5 pb-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${status === 'failed' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
              {view.complete ? (
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="m5 10 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : status === 'failed' ? (
                <span className="text-lg font-bold" aria-hidden="true">!</span>
              ) : <Spinner />}
            </div>
            <div className="min-w-0">
              <div className={`text-[10px] font-bold uppercase tracking-[0.12em] ${status === 'failed' ? 'text-red-600' : 'text-emerald-700'}`}>{view.eyebrow}</div>
              <h2 id="recipe-execution-guide-title" className="mt-0.5 truncate text-lg font-semibold tracking-tight text-gray-950">{view.title}</h2>
            </div>
            <button
              type="button"
              onClick={canMinimize ? onMinimize : onClose}
              className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label={canMinimize ? 'Minimize recipe progress' : 'Close recipe guide'}
              title={canMinimize ? 'Minimize' : 'Close'}
            >
              {canMinimize ? (
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M5 12.5h10" strokeLinecap="round" />
                </svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="m6 6 8 8m0-8-8 8" strokeLinecap="round" />
                </svg>
              )}
            </button>
          </div>

          {view.message && (
            <p className="mt-3 text-sm leading-5 text-gray-600">{view.message}</p>
          )}

          <div
            className="mt-5 grid"
            style={{ gridTemplateColumns: `repeat(${view.steps.length}, minmax(0, 1fr))` }}
            aria-label={`Progress: step ${view.step} of ${view.steps.length}`}
          >
            {view.steps.map((label, index) => {
              const isComplete = view.complete ? index < view.step : index < view.step - 1;
              const isCurrent = !view.complete && status !== 'failed' && index === view.step - 1;
              const isFailed = status === 'failed' && index === view.step - 1;
              const connectorComplete = index < view.step - 1;

              return (
                <div key={label} className="relative flex min-w-0 flex-col items-center gap-1.5 text-center">
                  {index < view.steps.length - 1 && (
                    <span className={`absolute left-1/2 top-[11px] h-0.5 w-full ${connectorComplete ? 'bg-emerald-600' : 'bg-gray-200'}`} aria-hidden="true" />
                  )}
                  <span
                    className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                      isComplete
                        ? 'border-emerald-600 bg-emerald-600 text-white'
                        : isCurrent
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                          : isFailed
                            ? 'border-red-500 bg-red-50 text-red-600'
                            : 'border-gray-200 bg-white text-gray-400'
                    }`}
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    {isComplete ? (
                      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                        <path d="m5 10 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : isCurrent ? <StepSpinner /> : isFailed ? '!' : index + 1}
                  </span>
                  <span className={`max-w-[5rem] px-1 text-[10px] leading-3 ${isComplete || isCurrent ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-7">
            <h3 className="text-[13px] font-semibold text-gray-950">{actionIntro.title}</h3>
            <p className="mt-0.5 text-xs leading-4 text-gray-500">{actionIntro.message}</p>
          </div>

          <div className="mt-2.5 grid gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1.5">
            <button type="button" onClick={() => { dismissForAction(); onAskChef(); }} className="flex items-center gap-3 rounded-lg p-2 text-left transition hover:bg-white">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-bold text-emerald-700">?</span>
              <span className="grid gap-0.5 text-xs font-semibold text-gray-900">Ask the chef<span className="text-[11px] font-normal text-gray-500">Questions about this recipe or its execution</span></span>
              <span className="ml-auto text-lg text-gray-400" aria-hidden="true">›</span>
            </button>
            <button type="button" onClick={() => { dismissForAction(); onViewLiveOutput(); }} className="flex items-center gap-3 rounded-lg p-2 text-left transition hover:bg-white">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-bold text-emerald-700">▤</span>
              <span className="grid gap-0.5 text-xs font-semibold text-gray-900">View live output<span className="text-[11px] font-normal text-gray-500">{liveOutputReady ? 'Follow detailed execution activity' : 'Activity will appear when execution begins'}</span></span>
              <span className="ml-auto text-lg text-gray-400" aria-hidden="true">›</span>
            </button>
            <button type="button" onClick={() => { dismissForAction(); onKeepEditing(); }} className="flex items-center gap-3 rounded-lg p-2 text-left transition hover:bg-white">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-bold text-emerald-700">✎</span>
              <span className="grid gap-0.5 text-xs font-semibold text-gray-900">Keep editing<span className="text-[11px] font-normal text-gray-500">Changes can be applied to the next execution</span></span>
              <span className="ml-auto text-lg text-gray-400" aria-hidden="true">›</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RecipeExecutionGuide;
